import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Alert, Spinner, Modal, Form, Badge, Row, Col } from 'react-bootstrap';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { siteConfigAPI } from '../../services/api';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
  EyeSlashIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/solid';

interface FAQ {
  id: number;
  topic: number;
  question: string;
  answer: string;
  sort_order: number;
  is_published: boolean;
}

interface Topic {
  id: number;
  title: string;
  description: string;
  icon: string;
  color: string;
  sort_order: number;
  is_published: boolean;
  faq_count?: number;
  faqs?: FAQ[];
}

const ICON_OPTIONS = [
  { value: 'question-mark-circle', label: 'Getting Started' },
  { value: 'cube', label: 'Products' },
  { value: 'user-group', label: 'Customers' },
  { value: 'shopping-cart', label: 'Orders' },
  { value: 'truck', label: 'Delivery' },
  { value: 'chart-bar', label: 'Reports' },
  { value: 'credit-card', label: 'Billing' },
  { value: 'cog-6-tooth', label: 'Settings' },
  { value: 'shield-check', label: 'Security' },
  { value: 'lifebuoy', label: 'Support' },
];

const COLOR_PRESETS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ec4899', '#6366f1', '#64748b', '#ef4444', '#0ea5e9'];

const AdminHelpCenter: React.FC = () => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Topic modal
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null);
  const [topicForm, setTopicForm] = useState({ title: '', description: '', icon: 'question-mark-circle', color: '#3b82f6', sort_order: 0 });
  const [saving, setSaving] = useState(false);

  // FAQ management
  const [expandedTopic, setExpandedTopic] = useState<number | null>(null);
  const [topicFAQs, setTopicFAQs] = useState<FAQ[]>([]);
  const [loadingFAQs, setLoadingFAQs] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', sort_order: 0 });
  const [savingFAQ, setSavingFAQ] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'topic' | 'faq'; id: number; label: string } | null>(null);

  const loadTopics = useCallback(async () => {
    try {
      const res = await siteConfigAPI.getHelpTopics();
      setTopics(res.data);
    } catch {
      setError('Failed to load help topics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTopics(); }, [loadTopics]);

  const loadFAQs = async (topicId: number) => {
    setLoadingFAQs(true);
    try {
      const res = await siteConfigAPI.getHelpTopic(topicId);
      setTopicFAQs(res.data.faqs || []);
    } catch {
      setError('Failed to load FAQs');
    } finally {
      setLoadingFAQs(false);
    }
  };

  const handleExpandTopic = (topicId: number) => {
    if (expandedTopic === topicId) {
      setExpandedTopic(null);
      setTopicFAQs([]);
    } else {
      setExpandedTopic(topicId);
      loadFAQs(topicId);
    }
  };

  // ─── Topic CRUD ───
  const openTopicModal = (topic?: Topic) => {
    if (topic) {
      setEditingTopic(topic);
      setTopicForm({ title: topic.title, description: topic.description, icon: topic.icon, color: topic.color, sort_order: topic.sort_order });
    } else {
      setEditingTopic(null);
      setTopicForm({ title: '', description: '', icon: 'question-mark-circle', color: '#3b82f6', sort_order: topics.length });
    }
    setShowTopicModal(true);
  };

  const handleSaveTopic = async () => {
    if (!topicForm.title.trim()) return;
    setSaving(true);
    try {
      if (editingTopic) {
        await siteConfigAPI.updateHelpTopic(editingTopic.id, topicForm);
        setSuccess('Topic updated');
      } else {
        await siteConfigAPI.createHelpTopic(topicForm);
        setSuccess('Topic created');
      }
      setShowTopicModal(false);
      loadTopics();
    } catch {
      setError('Failed to save topic');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleTopicPublished = async (topic: Topic) => {
    try {
      await siteConfigAPI.updateHelpTopic(topic.id, { is_published: !topic.is_published });
      loadTopics();
    } catch {
      setError('Failed to update topic');
    }
  };

  // ─── FAQ CRUD ───
  const openFAQModal = (faq?: FAQ) => {
    if (faq) {
      setEditingFAQ(faq);
      setFaqForm({ question: faq.question, answer: faq.answer, sort_order: faq.sort_order });
    } else {
      setEditingFAQ(null);
      setFaqForm({ question: '', answer: '', sort_order: topicFAQs.length });
    }
    setShowFAQModal(true);
  };

  const handleSaveFAQ = async () => {
    if (!faqForm.question.trim() || !faqForm.answer.trim() || !expandedTopic) return;
    setSavingFAQ(true);
    try {
      if (editingFAQ) {
        await siteConfigAPI.updateHelpFAQ(editingFAQ.id, faqForm);
        setSuccess('FAQ updated');
      } else {
        await siteConfigAPI.createHelpFAQ({ ...faqForm, topic: expandedTopic });
        setSuccess('FAQ created');
      }
      setShowFAQModal(false);
      loadFAQs(expandedTopic);
      loadTopics(); // refresh faq_count
    } catch {
      setError('Failed to save FAQ');
    } finally {
      setSavingFAQ(false);
    }
  };

  const handleToggleFAQPublished = async (faq: FAQ) => {
    try {
      await siteConfigAPI.updateHelpFAQ(faq.id, { is_published: !faq.is_published });
      if (expandedTopic) loadFAQs(expandedTopic);
    } catch {
      setError('Failed to update FAQ');
    }
  };

  // ─── Delete ───
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === 'topic') {
        await siteConfigAPI.deleteHelpTopic(deleteTarget.id);
        setSuccess('Topic deleted');
        if (expandedTopic === deleteTarget.id) {
          setExpandedTopic(null);
          setTopicFAQs([]);
        }
        loadTopics();
      } else {
        await siteConfigAPI.deleteHelpFAQ(deleteTarget.id);
        setSuccess('FAQ deleted');
        if (expandedTopic) {
          loadFAQs(expandedTopic);
          loadTopics();
        }
      }
    } catch {
      setError('Failed to delete');
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column min-vh-100 app-background">
        <Header />
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100 app-background">
      <Header />
      <div className="flex-grow-1">
        <Container className="py-3 py-md-5">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="display-5 fw-bold text-primary mb-1">Help Center</h1>
              <p className="text-muted mb-0">Manage topics and FAQs shown on the public help page</p>
            </div>
            <Button variant="primary" onClick={() => openTopicModal()} className="d-flex align-items-center gap-2">
              <PlusIcon style={{ width: 18, height: 18 }} /> New Topic
            </Button>
          </div>

          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {topics.length === 0 ? (
            <Card className="shadow-sm text-center py-5">
              <Card.Body>
                <QuestionMarkCircleIcon style={{ width: 48, height: 48, color: '#cbd5e1' }} className="mb-3" />
                <h5 className="text-muted">No help topics yet</h5>
                <p className="text-muted mb-3">Create your first topic to start building the help center.</p>
                <Button variant="primary" onClick={() => openTopicModal()}>Create Topic</Button>
              </Card.Body>
            </Card>
          ) : (
            topics.map(topic => (
              <Card key={topic.id} className="shadow-sm mb-3">
                <Card.Body className="py-3">
                  {/* Topic row */}
                  <div className="d-flex align-items-center gap-3">
                    <div
                      style={{
                        width: 40, height: 40, borderRadius: '10px',
                        background: `${topic.color}20`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}
                    >
                      <div style={{ width: 20, height: 20, borderRadius: '50%', background: topic.color }} />
                    </div>
                    <div className="flex-grow-1" style={{ cursor: 'pointer' }} onClick={() => handleExpandTopic(topic.id)}>
                      <div className="d-flex align-items-center gap-2">
                        <h6 className="mb-0 fw-bold">{topic.title}</h6>
                        <Badge bg={topic.is_published ? 'success' : 'secondary'} className="fw-normal" style={{ fontSize: '0.7rem' }}>
                          {topic.is_published ? 'Published' : 'Draft'}
                        </Badge>
                        <Badge bg="light" text="dark" className="fw-normal" style={{ fontSize: '0.7rem' }}>
                          {topic.faq_count ?? 0} FAQ{(topic.faq_count ?? 0) !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                      <small className="text-muted">{topic.description}</small>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <Button variant="link" size="sm" className="p-1" title={topic.is_published ? 'Unpublish' : 'Publish'} onClick={() => handleToggleTopicPublished(topic)}>
                        {topic.is_published
                          ? <EyeIcon style={{ width: 16, height: 16, color: '#10b981' }} />
                          : <EyeSlashIcon style={{ width: 16, height: 16, color: '#94a3b8' }} />}
                      </Button>
                      <Button variant="link" size="sm" className="p-1" title="Edit" onClick={() => openTopicModal(topic)}>
                        <PencilSquareIcon style={{ width: 16, height: 16, color: '#3b82f6' }} />
                      </Button>
                      <Button variant="link" size="sm" className="p-1" title="Delete" onClick={() => setDeleteTarget({ type: 'topic', id: topic.id, label: topic.title })}>
                        <TrashIcon style={{ width: 16, height: 16, color: '#ef4444' }} />
                      </Button>
                      <Button variant="link" size="sm" className="p-1" onClick={() => handleExpandTopic(topic.id)}>
                        {expandedTopic === topic.id
                          ? <ChevronUpIcon style={{ width: 16, height: 16, color: '#64748b' }} />
                          : <ChevronDownIcon style={{ width: 16, height: 16, color: '#64748b' }} />}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded FAQs */}
                  {expandedTopic === topic.id && (
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e2e8f0' }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>FAQs</h6>
                        <Button variant="outline-primary" size="sm" onClick={() => openFAQModal()} className="d-flex align-items-center gap-1" style={{ fontSize: '0.8rem' }}>
                          <PlusIcon style={{ width: 14, height: 14 }} /> Add FAQ
                        </Button>
                      </div>
                      {loadingFAQs ? (
                        <div className="text-center py-3"><Spinner size="sm" animation="border" variant="primary" /></div>
                      ) : topicFAQs.length === 0 ? (
                        <p className="text-muted text-center py-2 mb-0" style={{ fontSize: '0.85rem' }}>No FAQs yet. Add one above.</p>
                      ) : (
                        topicFAQs.map((faq, i) => (
                          <div key={faq.id} className="d-flex align-items-start gap-2 py-2" style={{ borderBottom: i < topicFAQs.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center gap-2">
                                <strong style={{ fontSize: '0.88rem' }}>{faq.question}</strong>
                                {!faq.is_published && <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>Draft</Badge>}
                              </div>
                              <p className="mb-0 text-muted" style={{ fontSize: '0.82rem', lineHeight: 1.5 }}>{faq.answer.length > 150 ? faq.answer.substring(0, 150) + '...' : faq.answer}</p>
                            </div>
                            <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                              <Button variant="link" size="sm" className="p-1" onClick={() => handleToggleFAQPublished(faq)}>
                                {faq.is_published
                                  ? <EyeIcon style={{ width: 14, height: 14, color: '#10b981' }} />
                                  : <EyeSlashIcon style={{ width: 14, height: 14, color: '#94a3b8' }} />}
                              </Button>
                              <Button variant="link" size="sm" className="p-1" onClick={() => openFAQModal(faq)}>
                                <PencilSquareIcon style={{ width: 14, height: 14, color: '#3b82f6' }} />
                              </Button>
                              <Button variant="link" size="sm" className="p-1" onClick={() => setDeleteTarget({ type: 'faq', id: faq.id, label: faq.question })}>
                                <TrashIcon style={{ width: 14, height: 14, color: '#ef4444' }} />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            ))
          )}
        </Container>
      </div>
      <Footer />

      {/* Topic Modal */}
      <Modal show={showTopicModal} onHide={() => setShowTopicModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingTopic ? 'Edit Topic' : 'New Topic'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control value={topicForm.title} onChange={e => setTopicForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g., Getting Started" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control value={topicForm.description} onChange={e => setTopicForm(p => ({ ...p, description: e.target.value }))} placeholder="Short description for the topic card" />
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Icon</Form.Label>
                <Form.Select value={topicForm.icon} onChange={e => setTopicForm(p => ({ ...p, icon: e.target.value }))}>
                  {ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sort Order</Form.Label>
                <Form.Control type="number" min={0} value={topicForm.sort_order} onChange={e => setTopicForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Color</Form.Label>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {COLOR_PRESETS.map(c => (
                <button
                  key={c}
                  onClick={() => setTopicForm(p => ({ ...p, color: c }))}
                  style={{
                    width: 28, height: 28, borderRadius: '8px', background: c, border: topicForm.color === c ? '3px solid #1f2937' : '2px solid transparent',
                    cursor: 'pointer', transition: 'border 0.15s',
                  }}
                />
              ))}
              <Form.Control
                type="text"
                value={topicForm.color}
                onChange={e => setTopicForm(p => ({ ...p, color: e.target.value }))}
                style={{ width: '90px', fontSize: '0.85rem' }}
                placeholder="#hex"
              />
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTopicModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveTopic} disabled={saving || !topicForm.title.trim()}>
            {saving ? <Spinner size="sm" animation="border" /> : editingTopic ? 'Save Changes' : 'Create Topic'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* FAQ Modal */}
      <Modal show={showFAQModal} onHide={() => setShowFAQModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingFAQ ? 'Edit FAQ' : 'New FAQ'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Question</Form.Label>
            <Form.Control value={faqForm.question} onChange={e => setFaqForm(p => ({ ...p, question: e.target.value }))} placeholder="e.g., How do I create an account?" />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Answer</Form.Label>
            <Form.Control as="textarea" rows={4} value={faqForm.answer} onChange={e => setFaqForm(p => ({ ...p, answer: e.target.value }))} placeholder="Write the answer..." />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Sort Order</Form.Label>
            <Form.Control type="number" min={0} value={faqForm.sort_order} onChange={e => setFaqForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} style={{ width: '100px' }} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFAQModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveFAQ} disabled={savingFAQ || !faqForm.question.trim() || !faqForm.answer.trim()}>
            {savingFAQ ? <Spinner size="sm" animation="border" /> : editingFAQ ? 'Save Changes' : 'Create FAQ'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete {deleteTarget?.type === 'topic' ? 'topic' : 'FAQ'} "<strong>{deleteTarget?.label}</strong>"?
          {deleteTarget?.type === 'topic' && <p className="text-danger mt-2 mb-0" style={{ fontSize: '0.85rem' }}>This will also delete all FAQs in this topic.</p>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminHelpCenter;
